import React from "react";
import Navbar from "../components/Navbar";
import ScrollReveal from "../components/ScrollReveal";
import SEO from "../components/SEO";
import {
  BsArrowsExpand,
  BsCheck2Circle,
  BsInfoCircle,
  BsGenderFemale,
  BsGenderMale,
} from "react-icons/bs";

const SizeTable = ({ title, gender, data, icon: Icon, colorClass }) => (
  <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden hover:shadow-md transition-shadow duration-300">
    <div
      className={`p-6 ${colorClass} text-white flex items-center justify-between`}
    >
      <div className="flex items-center gap-3">
        <Icon size={24} />
        <h3 className="text-xl font-bold uppercase tracking-wider">{title}</h3>
      </div>
      <span className="text-sm font-medium opacity-80">{gender}</span>
    </div>
    <div className="p-0">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-gray-50/50 dark:bg-slate-800/50">
            <th className="px-6 py-4 text-sm font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest border-b border-gray-100 dark:border-slate-800">
              Size (VN/EU)
            </th>
            <th className="px-6 py-4 text-sm font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest border-b border-gray-100 dark:border-slate-800">
              Chiều dài chân (cm)
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
          {data.map((item, idx) => (
            <tr
              key={idx}
              className="hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors group"
            >
              <td className="px-6 py-4 font-bold text-gray-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                Size {item.size}
              </td>
              <td className="px-6 py-4 text-gray-600 dark:text-slate-400 font-medium">
                {item.cm} cm
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SizeGuide = () => {
  const womenSize = [
    { size: "36", cm: "21.6 - 22.5" },
    { size: "37", cm: "22.6 - 23.5" },
    { size: "38", cm: "23.6 - 24.1" },
    { size: "39", cm: "24.2 - 25.1" },
    { size: "40", cm: "25.2 - 25.9" },
    { size: "41", cm: "26.0 - 26.7" },
    { size: "42", cm: "26.8 - 27.6" },
    { size: "43", cm: "27.7 - 28.5" },
    { size: "44", cm: "28.6 - 30.0" },
  ];

  const menSize = [
    { size: "36", cm: "21.6 - 22.5" },
    { size: "37", cm: "22.5 - 22.9" },
    { size: "38", cm: "23.0 - 23.8" },
    { size: "39", cm: "23.9 - 24.5" },
    { size: "40", cm: "24.6 - 25.3" },
    { size: "41", cm: "25.4 - 26.1" },
    { size: "42", cm: "26.2 - 26.8" },
    { size: "43", cm: "26.9 - 27.7" },
    { size: "44", cm: "27.8 - 28.5" },
  ];
  return (
    <div 
      className="bg-gray-50 dark:bg-slate-950 min-h-screen flex flex-col transition-colors duration-500"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <SEO
        title="Hướng dẫn chọn size"
        description="Bảng đối chiếu size giày chuẩn cho Nam và Nữ giúp bạn chọn được đôi giày vừa vặn nhất tại TaHa Shoes."
      />
      <Navbar />

      <main className="flex-grow pt-44 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Section */}
          <ScrollReveal variants="fade-up">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight uppercase">
                Hướng Dẫn{" "}
                <span className="text-blue-600 dark:text-blue-500">
                  Chọn Size Giày
                </span>
              </h1>
              <div className="w-24 h-1.5 bg-blue-600 mx-auto rounded-full mb-8" />

              <div className="max-w-3xl mx-auto bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 p-8 rounded-3xl text-gray-700 dark:text-slate-300 leading-relaxed shadow-sm">
                <div className="flex items-start gap-4 text-left">
                  <div className="p-3 bg-blue-600 text-white rounded-2xl flex-shrink-0">
                    <BsArrowsExpand size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Cách đo kích thước bàn chân
                    </h2>
                    <p className="text-gray-600 dark:text-slate-400">
                      Bạn dùng thước đo chiều dài bàn chân của mình từ{" "}
                      <strong className="text-blue-600">GÓT CHÂN</strong> đến{" "}
                      <strong className="text-blue-600">
                        ĐẦU NGÓN CHÂN CÁI
                      </strong>
                      , sau đó đối chiếu với bảng size giày chuẩn bên dưới để
                      tìm ra số size phù hợp nhất nhé!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Tables Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Women's Table */}
            <ScrollReveal variants="fade-up" delay={0.1}>
              <SizeTable
                title="Bảng Size Giày Nữ"
                gender="Dành cho Nữ"
                data={womenSize}
                icon={BsGenderFemale}
                colorClass="bg-gradient-to-r from-pink-500 to-rose-400"
              />
            </ScrollReveal>

            {/* Men's Table */}
            <ScrollReveal variants="fade-up" delay={0.2}>
              <SizeTable
                title="Bảng Size Giày Nam"
                gender="Dành cho Nam"
                data={menSize}
                icon={BsGenderMale}
                colorClass="bg-gradient-to-r from-blue-700 to-indigo-600"
              />
            </ScrollReveal>
          </div>

          {/* Additional Tips */}
          <ScrollReveal variants="fade-up" threshold={0.1}>
            <div className="mt-16 bg-white dark:bg-slate-900 p-8 rounded-3xl border border-gray-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-center gap-6">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-500 dark:text-amber-400 rounded-full">
                <BsInfoCircle size={32} />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                  Mẹo nhỏ cho bạn:
                </h4>
                <p className="text-gray-600 dark:text-slate-400">
                  Nếu chiều dài bàn chân của bạn nằm giữa 2 size, TaHa <span className="text-red-600 font-bold">Shoes</span>
                  khuyên bạn nên chọn{" "}
                  <span className="font-bold text-blue-600 dark:text-blue-400">
                    Size lớn hơn
                  </span>{" "}
                  để đảm bảo sự thoải mái nhất khi di chuyển, đặc biệt là khi
                  mang tất (vớ).
                </p>
              </div>
              <div className="flex items-center gap-2 px-6 py-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold rounded-full">
                <BsCheck2Circle />
                Hỗ trợ 24/7
              </div>
            </div>
          </ScrollReveal>
        </div>
      </main>
    </div>
  );
};

export default SizeGuide;
